import * as React from 'react';
import type { PickerOption } from '@sdk/components/forms/types';
import useModalState from '@sdk/components/ui/modal/useModalState';
import gettext from '@sdk/lang/gettext';
import useSlingApi from '@sdk/api/useSlingApi';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useAuth from '@sdk/authentication/useAuth';
import type { DocumentData } from '@sdk/users/types/DocumentData';

export const useDocumentListItem = (
  document: DocumentData,
  onRemoved: () => void,
  onEdit: (document: DocumentData) => void,
  formRef: React.MutableRefObject<any>,
  previewRef: React.MutableRefObject<any>
) => {
  const deleteConfirmationModalState = useModalState();
  const api = useSlingApi();
  const { showErrorToast } = useToast();
  const { currentUser } = useAuth();

  const moreOptions: PickerOption[] = [
    ...(currentUser.isManager
      ? [
          {
            value: 'edit',
            label: gettext('Edit'),
          },
        ]
      : []),
    ...(document.mimetype?.startsWith('image')
      ? [
          {
            value: 'preview',
            label: gettext('Preview'),
          },
        ]
      : []),
    {
      value: 'download',
      label: gettext('Download'),
    },
    ...(currentUser.isManager
      ? [
          {
            value: 'delete',
            label: gettext('Delete'),
          },
        ]
      : []),
  ];

  const onOptionClick = (option: PickerOption) => {
    switch (option.value) {
      case 'preview':
        return void previewRef.current.open();
      case 'download':
        return void formRef.current.submit();
      case 'edit':
        return void onEdit(document);
      case 'delete':
        return void deleteConfirmationModalState.open();
    }
  };

  const remove = async () => {
    try {
      await api.users.deleteDocument(document.user.id, document.id);
      onRemoved();
    } catch (e) {
      showErrorToast(getErrorForToast(e));
      throw e;
    }
  };

  return {
    moreOptions,
    onOptionClick,
    deleteConfirmationModalState,
    remove,
  };
};
